import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { SCENE_CONFIG } from './config.js';

export class VirtualRoom {
  constructor(container) {
    this.container = container;
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
    this.animationId = null;
    this.onUpdate = null;
    this.clock = new THREE.Clock();

    this.init();
  }

  init() {
    // Cria a cena
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(SCENE_CONFIG.backgroundColor);

    // Configura a câmera
    const aspect = window.innerWidth / window.innerHeight;
    this.camera = new THREE.PerspectiveCamera(
      SCENE_CONFIG.camera.fov,
      aspect,
      SCENE_CONFIG.camera.near,
      SCENE_CONFIG.camera.far
    );
    
    this.camera.position.set(
      SCENE_CONFIG.camera.position.x,
      SCENE_CONFIG.camera.position.y,
      SCENE_CONFIG.camera.position.z
    );

    // Configura o renderer
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.container.appendChild(this.renderer.domElement);

    // Adiciona controles orbitais
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.minDistance = 5;
    this.controls.maxDistance = 50;
    this.controls.maxPolarAngle = Math.PI / 2;

    // Adiciona iluminação
    this.setupLighting();

    // Adiciona grid e chão
    this.setupEnvironment();

    // Event listener para redimensionamento
    window.addEventListener('resize', () => this.onWindowResize());

    // Inicia animação
    this.animate();
  }

  setupLighting() {
    // Luz ambiente
    const ambientLight = new THREE.AmbientLight(
      SCENE_CONFIG.lighting.ambient.color,
      SCENE_CONFIG.lighting.ambient.intensity
    );
    this.scene.add(ambientLight);

    // Luz direcional
    const directionalLight = new THREE.DirectionalLight(
      SCENE_CONFIG.lighting.directional.color,
      SCENE_CONFIG.lighting.directional.intensity
    );
    directionalLight.position.set(
      SCENE_CONFIG.lighting.directional.position.x,
      SCENE_CONFIG.lighting.directional.position.y,
      SCENE_CONFIG.lighting.directional.position.z
    );
    directionalLight.castShadow = true;
    this.scene.add(directionalLight);
  }

  setupEnvironment() {
    // Adiciona um chão
    const floorGeometry = new THREE.PlaneGeometry(50, 50);
    const floorMaterial = new THREE.MeshStandardMaterial({
      color: 0x16213e,
      roughness: 0.8,
      metalness: 0.2
    });
    const floor = new THREE.Mesh(floorGeometry, floorMaterial);
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    this.scene.add(floor);

    // Adiciona um grid auxiliar
    const gridHelper = new THREE.GridHelper(50, 50, 0x0f3460, 0x0a2647);
    gridHelper.position.y = 0.01;
    this.scene.add(gridHelper);

    // Adiciona eixos auxiliares (opcional, para debugging)
    // const axesHelper = new THREE.AxesHelper(5);
    // this.scene.add(axesHelper);
  }

  onWindowResize() {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  }

  animate() {
    this.animationId = requestAnimationFrame(() => this.animate());

    const deltaTime = this.clock.getDelta();

    // Chama callback de update (para atualizar jogadores)
    if (this.onUpdate) {
      this.onUpdate(deltaTime);
    }

    // Atualiza controles
    this.controls.update();

    // Renderiza a cena
    this.renderer.render(this.scene, this.camera);
  }

  addObject(object) {
    this.scene.add(object);
  }

  removeObject(object) {
    this.scene.remove(object);
  }

  dispose() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    
    window.removeEventListener('resize', () => this.onWindowResize());
    
    this.renderer.dispose();
    this.controls.dispose();
  }
}
